<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Question set results</h1>
            <p class="lead">View pupil results for a specific question set.</p>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel">
                <TeacherDateFilter
                    :default-interval="dateInterval"
                    :intervals="predefinedIntervals.filter((i) => i.title !== 'All time')"
                    @change="
                        (newInterval: DateInterval) => {
                            dateInterval = newInterval;
                        }
                    "
                />
                <v-autocomplete
                    v-model="selectedQuestionSet"
                    :clearable="true"
                    label="Select a question set"
                    :items="questionSets"
                    item-title="title"
                    item-value="taskId"
                    return-object
                >
                    <template #item="{ props, item }">
                        <v-list-item
                            v-bind="props"
                            :title="item.raw.bookTitle"
                            :subtitle="item.raw.taskTitle"
                        ></v-list-item>
                    </template>
                </v-autocomplete>
            </div>

            <v-alert v-if="selectedQuestionSet && !loading && items.length === 0" type="info">
                No data to view. Try adjusting the time period or choose another question set.
            </v-alert>
            <div v-if="items.length > 0" class="panel">
                <div>
                    <v-data-table
                        v-model:expanded="expanded"
                        items-per-page="-1"
                        :headers="headers"
                        :items="items"
                        class="elevation-0"
                    >
                        <template #bottom></template>
                        <template #item="{ item }">
                            <tr>
                                <td>
                                    <RouterLink :to="`/teacher/pupil/${item.pupilId}`">
                                        {{
                                            teacher && item.pupilId === teacher.virtual_pupil.id
                                                ? 'My virtual pupil'
                                                : item.pupilName
                                        }}
                                    </RouterLink>
                                    <v-btn
                                        v-if="item.previous.length > 0"
                                        type="button"
                                        :icon="expanded.includes(item.id) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                                        variant="text"
                                        size="small"
                                        @click="expand(item.id)"
                                    >
                                    </v-btn>
                                </td>
                                <TeacherReportingQuestionSetPartialTableRow :item="item" :stepTypes="stepTypes" />
                            </tr>
                        </template>

                        <template #expanded-row="{ item }">
                            <tr v-for="row of item.previous" :key="`expanded-${row.pupilTaskId}`">
                                <td></td>
                                <TeacherReportingQuestionSetPartialTableRow :item="row" :stepTypes="stepTypes" />
                            </tr>
                        </template>
                    </v-data-table>
                </div>
                <div class="d-flex justify-end mt-5">
                    <ExcelDownload :data="items" :columns="xlsColumns" :file-name="xlsFilename" report="question-set">
                        <v-btn size="small" variant="outlined"
                            >Download Excel
                            <v-icon right>mdi-download</v-icon>
                        </v-btn>
                    </ExcelDownload>
                </div>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.pupilTasks" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { DateInterval, TableRowItem } from '~/types/frontend';
import { predefinedIntervals } from '~/utils/useDateFilters';
import { PupilTask } from '@ilteducation/giglets-types';

const { formatDate, formatDateField, schoolStart } = useDateFormat();

definePage({ meta: { area: 'reporting' } });

const store = useTeacherStore();
store.fetchPupilTasks();
store.fetchTeacher();
store.fetchPupils();
const { pupilTasks, teacher, isLoaded } = storeToRefs(store);
const { pupilName } = store;

const expanded = ref<string[]>([]);
const loading = ref(true);

const expand = (id: string) => {
    if (expanded.value.includes(id)) {
        expanded.value = expanded.value.filter((i) => i !== id);
    } else {
        expanded.value.push(id);
    }
};

type QuestionSet = {
    taskId: number;
    bookTitle: string;
    taskTitle: string;
    title: string;
};

const questionSets = computed(() => {
    const res: Record<number, QuestionSet> = {};
    pupilTasks.value
        ?.filter((pt) => pt.task.reading_test)
        .map((pt) => ({
            taskId: pt.task.id,
            bookTitle: pt.task.book.title || '',
            taskTitle: pt.task.title,
            title: `${pt.task.book.title} - ${pt.task.title}`,
        }))
        .forEach((qs) => {
            res[qs.taskId] = qs;
        });
    const values = Object.values(res);
    values.sort((a, b) => {
        return a.title.localeCompare(b.title);
    });
    return values;
});

const selectedQuestionSet = ref<QuestionSet>();
const dateInterval = ref<DateInterval>({
    from: formatDateField(schoolStart.toJSDate()),
});

const stepTypes = computed(() => {
    if (pupilTaskResults.value.length === 0 || !pupilTaskResults.value[0].task.steps) {
        return [];
    }
    return pupilTaskResults.value[0].task.steps.map((step) => ({
        id: step.id,
        name: step.step_type.name,
    }));
});

const headers = computed(() => {
    return [
        { title: 'Pupil', key: 'pupilName', sortable: false },
        ...stepTypes.value.map((st, idx) => ({ title: `${idx + 1}. ${st.name}`, key: `${st.id}`, sortable: false })),
        { title: 'Total', key: 'total', sortable: false },
        { title: '', key: 'dates', sortable: false },
    ];
});

const formatXlsDate = (value: string) => {
    return value ? formatDate(value) : '';
};

const formatXlsStep = (stepId: number) => (results: TableRowItem['results']) => {
    return results[stepId]?.correct;
};

const xlsColumns = computed(() => {
    return [
        { label: 'Pupil', field: 'pupilName' },
        ...stepTypes.value.map((st) => ({ label: st.name, field: 'results', dataFormat: formatXlsStep(st.id) })),
        { label: 'Total Percentage', field: 'total' },
        { label: 'Assigned', field: 'assigned_date', dataFormat: formatXlsDate },
        { label: 'Finished', field: 'finished', dataFormat: formatXlsDate },
    ];
});

const xlsFilename = computed(() => {
    return `QuestionSet-${selectedQuestionSet.value?.bookTitle}-${selectedQuestionSet.value?.taskTitle}`;
});

const pupilTaskResults = ref<PupilTask[]>([]);

const items = computed(() => {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    pupilTaskResults.value.sort((a, b) => {
        if (!a.finished) {
            return -1;
        }
        if (!b.finished) {
            return 1;
        }
        return b.finished.localeCompare(a.finished);
    });

    const pupilResults: Record<number, TableRowItem> = {};
    for (const pt of pupilTaskResults.value) {
        const row: TableRowItem = {
            id: `${pt.id}`,
            pupilTaskId: pt.id,
            pupilId: pt.pupil.id,
            pupilName: pupilName(pt.pupil),
            results: {},
            total: 0,
            assigned_date: pt.assigned_date,
            finished: pt.finished,
            status: pt.status,
            previous: [],
        };
        let totalCorrect = 0;
        for (const pupilStep of pt.pupil_steps || []) {
            row.results[pupilStep.step.id] = {
                correct: pupilStep.correct,
                attempts: pupilStep.attempts,
            };
            totalCorrect += pupilStep.correct ? 1 : 0;
        }
        row.total = Math.round((totalCorrect / stepTypes.value.length) * 100);
        if (pupilResults[pt.pupil.id]) {
            pupilResults[pt.pupil.id].previous.push(row);
        } else {
            pupilResults[pt.pupil.id] = row;
        }
    }
    return Object.values(pupilResults);
});

const { query } = useRoute();
watch(
    () => query.taskId,
    (taskId) => {
        selectedQuestionSet.value = questionSets.value.find((qs) => qs.taskId === parseInt(String(taskId)));
    },
    { immediate: true },
);

watchEffect(async () => {
    let dateQuery = '';
    if (dateInterval.value.from) {
        dateQuery += `&from=${dateInterval.value.from}`;
    }
    if (dateInterval.value.to) {
        dateQuery += `&to=${dateInterval.value.to}`;
    }
    if (selectedQuestionSet.value) {
        loading.value = true;
        try {
            pupilTaskResults.value = await $fetch<PupilTask[]>(
                `/api/teacher/reporting/task_results?taskId=${selectedQuestionSet.value.taskId}${dateQuery}`,
            );
        } finally {
            loading.value = false;
        }
    }
});
</script>
<style scoped lang="scss">
:deep(.v-data-table__th) {
    height: 210px !important;
    white-space: nowrap;
    vertical-align: bottom;
}
:deep(.v-data-table-header__content) {
    transform: translate(-10px, -21px) rotate(305deg);
    width: 20px !important;
    display: block;
    font-family: HeinemannSpecial-Bold, serif;
}
:deep(td) {
    vertical-align: middle !important;
}
</style>
